import React, { useState } from 'react';
import { Card, Row, Col, Select, Button, Typography, Divider } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, StopOutlined } from '@ant-design/icons';
import M2MChart from './M2MChart';

const { Title, Text } = Typography;
const { Option } = Select;

interface Strategy {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'stopped';
  totalPnL: number;
  unrealizedPnL: number;
  exposure: number;
  startTime: string;
}

const StrategiesPage: React.FC = () => {
  const [selectedStrategy, setSelectedStrategy] = useState<string>('strategy-1');
  const [selectedInterval, setSelectedInterval] = useState<string>('1m');

  // Mock strategy data for demonstration
  const strategies: Strategy[] = [
    {
      id: 'strategy-1',
      name: 'Long Short Webhook Strategy',
      status: 'active',
      totalPnL: 15420.50,
      unrealizedPnL: 2340.25,
      exposure: 125000,
      startTime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'strategy-2', 
      name: 'Options Iron Condor',
      status: 'paused',
      totalPnL: -1250.75,
      unrealizedPnL: -450.00,
      exposure: 85000,
      startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'strategy-3',
      name: 'Momentum Breakout',
      status: 'active',
      totalPnL: 8750.00,
      unrealizedPnL: 1200.50,
      exposure: 200000,
      startTime: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    }
  ];

  const selectedStrategyData = strategies.find(s => s.id === selectedStrategy);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#52c41a';
      case 'paused': return '#faad14';
      case 'stopped': return '#8c8c8c';
      default: return '#8c8c8c';
    }
  };

  const getPnLColor = (pnl: number) => {
    return pnl >= 0 ? '#52c41a' : '#ff4d4f';
  };

  const handleStrategyAction = (action: 'pause' | 'resume' | 'stop') => {
    console.log(`${action} strategy:`, selectedStrategy);
    // TODO: Implement actual API calls to strategy lifecycle endpoints
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#141414',
      minHeight: '100vh',
      color: '#ffffff'
    }}>
      <Title level={2} style={{ color: '#ffffff', marginBottom: '24px' }}>
        Strategy Management Dashboard
      </Title>

      {/* Strategy Selection */}
      <Card 
        title="Active Strategies" 
        style={{ 
          backgroundColor: '#1f1f1f', 
          borderColor: '#303030',
          marginBottom: '24px'
        }}
        headStyle={{ 
          backgroundColor: '#1f1f1f', 
          borderBottomColor: '#303030', 
          color: '#ffffff' 
        }}
        bodyStyle={{ backgroundColor: '#1f1f1f' }}
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#8c8c8c' }}>Select Strategy:</Text>
            </div>
            <Select
              value={selectedStrategy}
              onChange={setSelectedStrategy}
              style={{ width: '100%' }}
              size="large"
            >
              {strategies.map(strategy => (
                <Option key={strategy.id} value={strategy.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{strategy.name}</span>
                    <span 
                      style={{ 
                        color: getStatusColor(strategy.status),
                        fontSize: '10px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {strategy.status}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={4}>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#8c8c8c' }}>Interval:</Text>
            </div>
            <Select
              value={selectedInterval}
              onChange={setSelectedInterval}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="1m">1 Minute</Option>
              <Option value="5m">5 Minutes</Option>
              <Option value="15m">15 Minutes</Option>
              <Option value="1h">1 Hour</Option>
              <Option value="1d">1 Day</Option>
            </Select>
          </Col>

          <Col span={12}>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ color: '#8c8c8c' }}>Strategy Controls:</Text>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button 
                icon={<PauseCircleOutlined />} 
                onClick={() => handleStrategyAction('pause')}
                style={{ backgroundColor: '#faad14', borderColor: '#faad14', color: '#000' }}
              >
                Pause
              </Button>
              <Button 
                icon={<PlayCircleOutlined />} 
                onClick={() => handleStrategyAction('resume')}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#000' }}
              >
                Resume
              </Button>
              <Button 
                icon={<StopOutlined />} 
                onClick={() => handleStrategyAction('stop')}
                danger
              >
                Stop
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Strategy Overview */}
      {selectedStrategyData && (
        <Card
          title={`${selectedStrategyData.name} - Overview`}
          style={{ 
            backgroundColor: '#1f1f1f', 
            borderColor: '#303030',
            marginBottom: '24px'
          }}
          headStyle={{ 
            backgroundColor: '#1f1f1f', 
            borderBottomColor: '#303030', 
            color: '#ffffff' 
          }}
          bodyStyle={{ backgroundColor: '#1f1f1f' }}
        >
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <div>
                <Text style={{ color: '#8c8c8c' }}>Status</Text>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: getStatusColor(selectedStrategyData.status),
                  textTransform: 'uppercase'
                }}>
                  {selectedStrategyData.status}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <Text style={{ color: '#8c8c8c' }}>Total P&L</Text>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: getPnLColor(selectedStrategyData.totalPnL)
                }}>
                  ₹{selectedStrategyData.totalPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <Text style={{ color: '#8c8c8c' }}>Unrealized P&L</Text>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: getPnLColor(selectedStrategyData.unrealizedPnL)
                }}>
                  ₹{selectedStrategyData.unrealizedPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <Text style={{ color: '#8c8c8c' }}>Total Exposure</Text>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: '#1890ff'
                }}>
                  ₹{selectedStrategyData.exposure.toLocaleString('en-IN')}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* M2M Chart */}
      <Card
        title="Mark-to-Market (M2M) Chart"
        style={{ 
          backgroundColor: '#1f1f1f', 
          borderColor: '#303030' 
        }}
        headStyle={{ 
          backgroundColor: '#1f1f1f', 
          borderBottomColor: '#303030', 
          color: '#ffffff' 
        }}
        bodyStyle={{ backgroundColor: '#1f1f1f', padding: '16px' }}
      >
        <M2MChart 
          strategyId={selectedStrategy}
          interval={selectedInterval}
          height={400}
        />
        
        <Divider style={{ borderColor: '#303030', margin: '16px 0' }} />
        
        <div style={{ textAlign: 'center', color: '#8c8c8c', fontSize: '12px' }}>
          <Text style={{ color: '#8c8c8c' }}>
            Real-time M2M updates every 30 seconds • Interval: {selectedInterval} • 
            Strategy: {selectedStrategyData?.name}
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default StrategiesPage;